<template>
  <div class="d-flex flex-row">
    <div class="main-select">
      <LeadCombobox
        v-model="selectedValue"
        :items="options"
        :get-option-label="getOptionLabel"
        :get-option-value="getOptionValue"
        :loading="isLoading"
        @search="onSearch"
        :label="$t('campaigns.google_ads.location')"
        placeholder="e.g. Zurich, Switzerland"
        :disable-filtering="true">
        <template #option="option">
          <div class="flex items-center justify-between w-full">
            <div>
              <div v-if="option.type === 'place'">
                {{ (option.value as LocationTargetingPlace).target_location_label }}, {{ option.value.target_type }},
                {{ option.value.target_country_label }}
              </div>
              <div v-if="option.type === 'position'">{{ (option.value as LocationTargetingPosition).name }}</div>
            </div>
          </div>
        </template>
      </LeadCombobox>
    </div>
    <template v-if="selectedValue">
      <!-- RADIUS: Only shows if the selected value is a 'position' type -->
      <div v-if="actualLocationData && typeof actualLocationData === 'object' && 'radius' in actualLocationData">
        <LeadInput
          type="number"
          step="1"
          min="1"
          :label="$t('campaigns.google_ads.radius')"
          v-model.number="(actualLocationData as LocationTargetingPosition).radius"
          class="ms-2" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, type PropType } from 'vue';
import type {
  LocationTargetingElement,
  LocationTargetingPlace,
  LocationTargetingPosition,
} from '@/types/locationTargeting';
import { request } from '@/api/client';
import LeadInput from './LeadInput.vue';
import LeadCombobox from './LeadCombobox.vue';

// --- PROPS & EMITS ---
const props = defineProps({
  modelValue: {
    type: Object as PropType<LocationTargetingPlace | LocationTargetingPosition | null>,
    default: undefined,
  },
  providerId: {
    type: [String, Number],
    required: true,
  },
  // offer address + radius results next to the places
  allowProximity: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:modelValue']);

const fetchPlaces = async (query: string): Promise<LocationTargetingPlace[]> => {
  if (!props.providerId) return [];
  try {
    const response = await request('GET', `/v1/account/campaigns/${props.providerId}/options/targeting_cities`, {
      query,
      country: 'Switzerland',
    });

    // Transform GoogleAdsLocation objects to LocationTargetingPlace objects
    return response.data.map((location: any) => ({
      target_location_label: location.name,
      target_location_id: location.id,
      target_country_label: location.countryCode,
      target_country_code: location.countryCode,
      target_type: location.targetType,
      bid_modifier: 1.0,
    }));
  } catch (e) {
    console.error(e);
    return [];
  }
};

const fetchPositions = async (query: string): Promise<LocationTargetingPosition[]> => {
  if (!query) return [];
  try {
    const response = await request('POST', '/v1/account/google-maps/geo-coding', undefined, { address: query });
    const results = response.data.results ?? [];
    return results.map((result: any) => ({
      name: result.formatted_address,
      latitude: result.geometry.location.lat,
      longitude: result.geometry.location.lng,
      radius: 10,
      radius_unit: 'KILOMETERS',
      bid_modifier: 1.0,
    }));
  } catch (e) {
    console.error(e);
    return [];
  }
};

function debounce<T extends (...args: any[]) => void>(func: T, delay: number) {
  let timeout: number;
  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    clearTimeout(timeout);
    timeout = window.setTimeout(() => func.apply(this, args), delay);
  };
}

const options = ref<LocationTargetingElement[]>([]);
const isLoading = ref(false);

const selectedValue = computed({
  get: () => props.modelValue ?? null,
  set: (value) => {
    // If the value is a LocationTargetingElement, extract the actual location data
    let actualValue: LocationTargetingPlace | LocationTargetingPosition | null = value || null;
    if (value && typeof value === 'object' && 'type' in value && 'value' in value) {
      actualValue = value.value as LocationTargetingPlace | LocationTargetingPosition;
    }
    emit('update:modelValue', actualValue);
    if (value) options.value = [];
  },
});

const actualLocationData = computed((): LocationTargetingPlace | LocationTargetingPosition | null => {
  if (!selectedValue.value) return null;

  // If it's a LocationTargetingElement (has 'value' property), extract the nested value
  if (typeof selectedValue.value === 'object' && 'value' in selectedValue.value && selectedValue.value.value) {
    return selectedValue.value.value as LocationTargetingPlace | LocationTargetingPosition;
  }

  // Else, it's already the direct location data
  return selectedValue.value as LocationTargetingPlace | LocationTargetingPosition;
});

// --- METHODS ---
const performSearch = async (searchQuery: string) => {
  if (searchQuery.length < 2) {
    options.value = [];
    return;
  }
  isLoading.value = true;
  try {
    const [places, positions] = await Promise.all([
      fetchPlaces(searchQuery),
      props.allowProximity ? fetchPositions(searchQuery) : Promise.resolve([]),
    ]);
    const placeElements: LocationTargetingElement[] = places.map((p, i) => ({
      type: 'place',
      key: `place-${i}`,
      index: i,
      value: p,
    }));
    const positionElements: LocationTargetingElement[] = positions.map((p, i) => ({
      type: 'position',
      key: `pos-${i}`,
      index: i,
      value: p,
    }));
    options.value = [...placeElements, ...positionElements];
  } catch (error) {
    console.error('Failed to fetch location data:', error);
    options.value = [];
  } finally {
    isLoading.value = false;
  }
};

const debouncedSearch = debounce((searchQuery: string) => {
  if (searchQuery) performSearch(searchQuery);
  else isLoading.value = false;
}, 500);

const onSearch = (searchQuery: string) => {
  isLoading.value = true;
  debouncedSearch(searchQuery);
};

const getOptionLabel = (option: any): string => {
  const value = 'value' in option ? option.value : option;
  if ('target_location_label' in value && value.target_location_label) {
    const parts = [value.target_location_label];
    if (value.target_type) parts.push(value.target_type);
    if (value.target_country_label) parts.push(value.target_country_label);
    return parts.join(', ');
  }
  if ('name' in value && value.name) return `${value.name} (+${value.radius} ${value.radius_unit.toLowerCase()})`;
  return '';
};

const getOptionValue = (option: any): string => {
  const value = 'value' in option ? option.value : option;

  if ('target_location_id' in value && value.target_location_id) {
    return `place-${value.target_location_id}`;
  }

  if ('latitude' in value && 'longitude' in value) {
    return `position-${value.latitude}-${value.longitude}`;
  }

  return '';
};
</script>

<style scoped>
.main-select {
  min-width: 250px;
  width: 100%;
}
</style>
